/* eslint-disable @typescript-eslint/no-explicit-any */
import CancelIcon from '@mui/icons-material/Cancel';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import React from 'react';

interface MultiSelectInputProps<
    T = { label: string; value: string; delete?: boolean },
> {
    options: T[];
    selectedValues: T[];
    onSelectedValuesChange: (values: T[]) => void;
    title: string;
    placeholder: string;
    disabled: boolean;
    disableAll?: boolean;
    handleDelete: (value: T) => void;
    getOptionLabel: (option: T) => string;
    isOptionEqualToValue?: (option: T, value: T) => boolean;
    size?: 'small' | 'medium';
    required?: boolean;
    /** Optional footer action inside the dropdown (e.g. Add New Service...) */
    addNewLabel?: string;
    onAddNew?: () => void;
}

function isPresent(value: unknown): boolean {
    return value !== undefined && value !== null;
}

function areDefinedAndEqual(left: unknown, right: unknown): boolean {
    return isPresent(left) && isPresent(right) && left === right;
}

function defaultIsOptionEqualToValue(option: any, value: any): boolean {
    return (
        areDefinedAndEqual(option?.value, value?.value) ||
        areDefinedAndEqual(option?.userId, value?.userId) ||
        areDefinedAndEqual(option?.id, value?.id) ||
        areDefinedAndEqual(option?.id, value?.unitId) ||
        areDefinedAndEqual(option?.unitId, value?.unitId) ||
        areDefinedAndEqual(option?.unitId, value?.id)
    );
}

export default function MultiSelectInput<T>({
    options,
    selectedValues,
    onSelectedValuesChange,
    title = 'Label',
    placeholder = 'Placeholder',
    disabled,
    handleDelete,
    disableAll,
    getOptionLabel,
    isOptionEqualToValue,
    size = 'small',
    addNewLabel,
    onAddNew,
}: MultiSelectInputProps<T>) {
    const showAddNew = Boolean(addNewLabel && onAddNew) && !disableAll;

    return (
        <Stack spacing={3} sx={{ width: '100%' }}>
            <Autocomplete
                multiple
                id="tags-outlined"
                options={options}
                getOptionLabel={getOptionLabel}
                isOptionEqualToValue={
                    isOptionEqualToValue || defaultIsOptionEqualToValue
                }
                value={selectedValues ?? []}
                onChange={(event, newValue) => {
                    onSelectedValuesChange(newValue);
                }}
                filterSelectedOptions
                clearIcon={false}
                disabled={disabled || disableAll}
                PaperComponent={
                    showAddNew
                        ? ({ children, ...other }) => (
                              <Paper {...other}>
                                  {children}
                                  <Button
                                      fullWidth
                                      size="small"
                                      onMouseDown={(e) => {
                                          // Keep focus behavior from closing before click registers
                                          e.preventDefault();
                                          onAddNew?.();
                                      }}
                                      sx={{
                                          justifyContent: 'flex-start',
                                          px: 2,
                                          py: 1,
                                          borderRadius: 0,
                                          textTransform: 'none',
                                          borderTop: '1px solid #e0e0e0',
                                      }}
                                  >
                                      {addNewLabel}
                                  </Button>
                              </Paper>
                          )
                        : undefined
                }
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={title}
                        placeholder={placeholder}
                        disabled={disabled || disableAll}
                    />
                )}
                size={size}
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                        // eslint-disable-next-line react/jsx-key
                        <Chip
                            label={getOptionLabel(option)}
                            {...getTagProps({ index })}
                            onDelete={() => handleDelete(option)}
                            deleteIcon={<CancelIcon />}
                            color={'success'}
                            disabled={disabled || disableAll}
                        />
                    ))
                }
            />
        </Stack>
    );
}
